import { Playground } from "@/components/Playground2";
import MessagesSettings from "@/components/Playground2/Messages/MessagesHeader/MessagesSettings";
import MessagesShimmer from "@/components/Playground2/Messages/MessagesShimmer";
import { TEMPLATE_FORMATS } from "@/components/Playground2/types";
import { Button } from "@/components/ui/button";
import TabNavigation from "@/components/ui/TabNavigation";
import { PromptVersion } from "@/types";
import { getTemplateFormat } from "@/utils/utils";
import {
  ClipboardListIcon as ClipboardListIconOutline,
  DocumentTextIcon as DocumentTextIconOutline,
  PencilIcon as PencilSquareIconOutline,
} from "@heroicons/react/outline";
import {
  ClipboardListIcon as ClipboardListIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
} from "@heroicons/react/solid";
import { UseQueryResult } from "@tanstack/react-query";
import { Stethoscope } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PromptTemplateVersionSelector from "./PromptTemplateVersionSelector";

import { useXrayMode } from "./hooks/useXrayMode";
import PromptTemplateAnalytics from "./PromptTemplateAnalytics";

interface OverviewContentProps {
  promptVersion: UseQueryResult<PromptVersion>;
  versionId?: string;
  totalVersions: number;
  navigateToPlayground: () => void;
  getCurrentTabIndex: () => number;
  setShowVersions: (showVersions: boolean) => void;
  showVersions: boolean;
}

const OverviewContent = ({
  promptVersion,
  totalVersions,
  navigateToPlayground,
  getCurrentTabIndex,
  setShowVersions,
  showVersions,
}: OverviewContentProps) => {
  const { isXrayMode, setIsXrayMode, isXrayAvailable } =
    useXrayMode(promptVersion);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const { versionId } = useParams();

  useEffect(() => {
    setSelectedTabIndex(0);
  }, [versionId]);

  return (
    <div className="flex h-full flex-row gap-x-2">
      <PromptTemplateVersionSelector
        selectedVersionId={promptVersion.data?.id}
        setShowVersionList={setShowVersions}
        showVersionList={showVersions}
        className="z-20 mt-2"
      />
      <div className="flex h-full flex-1 flex-col overflow-auto">
        {promptVersion.data && !promptVersion.isLoading ? (
          <>
            <TabNavigation
              currentTabIndex={selectedTabIndex}
              handleTabChange={setSelectedTabIndex}
              actionButtons={
                <div className="relative bottom-1 flex items-center gap-x-2">
                  {isXrayAvailable && (
                    <Button
                      onClick={() => setIsXrayMode((prev: boolean) => !prev)}
                      variant={isXrayMode ? "default" : "outline"}
                      size="sm"
                    >
                      <Stethoscope
                        className="mr-1.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      X-Ray
                    </Button>
                  )}

                  {getCurrentTabIndex() === 0 && (
                    <Button
                      onClick={navigateToPlayground}
                      variant="outline"
                      size="sm"
                      aria-label="Edit in Playground"
                    >
                      <PencilSquareIconOutline
                        className="mr-1.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Edit
                    </Button>
                  )}
                  <MessagesSettings
                    readonly={true}
                    formats={TEMPLATE_FORMATS}
                    currentFormat={getTemplateFormat(
                      promptVersion.data.prompt_template,
                    )}
                    isCompletion={
                      promptVersion.data.prompt_template.type === "completion"
                    }
                    metadata={promptVersion.data.metadata ?? undefined}
                  />
                </div>
              }
              tabs={[
                {
                  id: 0,
                  name: "Content",
                  solidIcon: <DocumentTextIconSolid className="h-4 w-4" />,
                  outlineIcon: <DocumentTextIconOutline className="h-4 w-4" />,
                  content: (
                    <div className="flex h-full flex-col overflow-hidden">
                      <div className="z-10 flex w-full flex-col bg-white pl-3 pr-2 shadow-sm">
                        <div className="flex w-full items-center justify-between py-2">
                          <h2 className="flex items-center text-xs font-normal text-gray-700">
                            <span className="mr-1">Viewing Version</span>
                            <span className="font-medium text-gray-900">
                              {versionId}
                            </span>
                            <span className="mx-1 text-gray-500">of</span>
                            <span className="font-medium text-gray-900">
                              {totalVersions >= Number(versionId)
                                ? totalVersions
                                : versionId}
                            </span>
                          </h2>
                        </div>
                        {promptVersion.data?.commit_message && (
                          <div className="mb-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                            <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">
                              Commit Message
                            </h3>
                            <p className="text-sm text-gray-700">
                              {promptVersion.data.commit_message}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="h-full flex-1 overflow-auto">
                        <Playground
                          readonly
                          key={`${promptVersion.data.id}_${isXrayMode}`}
                          selectedTemplateVersion={promptVersion.data}
                          blueprint={{
                            prompt_template: isXrayMode
                              ? promptVersion.data.rendered_template
                              : promptVersion.data.prompt_template,
                            provider_base_url_name:
                              promptVersion.data.provider_base_url_name,
                            commit_message:
                              promptVersion.data.commit_message ?? undefined,
                            metadata: promptVersion.data.metadata,
                            report_id:
                              promptVersion.data.report_id ?? undefined,
                          }}
                        />
                      </div>
                    </div>
                  ),
                },
                {
                  id: 1,
                  name: "Analytics & Logs",
                  solidIcon: (
                    <ClipboardListIconSolid
                      className="mr-1.5 h-4 w-4"
                      aria-hidden="true"
                    />
                  ),
                  outlineIcon: (
                    <ClipboardListIconOutline
                      className="mr-1.5 h-4 w-4"
                      aria-hidden="true"
                    />
                  ),
                  content: <PromptTemplateAnalytics />,
                },
              ]}
            />
          </>
        ) : (
          <div className="h-full">
            <MessagesShimmer />
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewContent;
